import { FC } from "react";
import { useRouteError } from "react-router-dom";
import styled from "@emotion/styled";
import { logger } from "@/modules/logger";
import { MDSTextVariant } from "@/mds/components/text/text/text.types";
import { ErrorLoadingIllustration } from "@/assets/illustrations/error-loading-illustration";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons/button";
import { forceReloadWindow } from "@/modules/window/reload";

/**
 * Router-level error boundary component that integrates with React Router's error handling.
 * This catches errors at the routing level and displays a full-page error state.
 */
export const RouterErrorBoundary: FC = () => {
  const error = useRouteError();

  // Log the error
  logger.error({
    message: "[RouterErrorBoundary] Error boundary triggered",
    info: {
      error: error instanceof Error ? error.message : String(error),
      ...(error instanceof Error && error.stack ? { stack: error.stack } : {}),
    },
  });

  return (
    <StyledContainer>
      <StyledContent>
        <StyledIllustrationWrapper>
          <ErrorLoadingIllustration />
        </StyledIllustrationWrapper>
        <StyledCaption>
          <StyledHeading>Something went wrong</StyledHeading>
          <StyledDescription>
            Mem ran into an unexpected error. Try reloading to continue.
          </StyledDescription>
          <StyledButtonContainer>
            <MDSButton variant={MDSButtonVariant.Contrast} onPress={forceReloadWindow}>
              Reload
            </MDSButton>
          </StyledButtonContainer>
        </StyledCaption>
      </StyledContent>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  align-items: center;
  justify-content: center;
`;

const StyledIllustrationWrapper = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.icon.placeholder};
`;

const StyledCaption = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
  text-align: center;
`;

const StyledHeading = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextM]};
  font-weight: ${({ theme }) => theme.fontWeights.smedium};
  line-height: ${({ theme }) => theme.lineHeights[MDSTextVariant.TextSm]};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  white-space: pre;
`;

const StyledDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights[MDSTextVariant.TextXs]};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin: 0;
  max-width: 449px;
  text-align: center;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: flex-start;
  padding-top: ${({ theme }) => theme.spacing[2]};
`;
